import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { StatCard } from '@/pages/Organization/Analytics/components/StatCard'
import { adminApi } from '@/utils/api'
import type { AdminUser } from '@/types/admin'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'

export default function AdminUsersPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [addOpen, setAddOpen] = useState(false)
  const [addForm, setAddForm] = useState({
    email: '',
    phoneNumber: '',
    password: '',
    firstName: '',
    lastName: '',
    notes: '',
  })
  const [addError, setAddError] = useState<string | null>(null)
  const [addSuccess, setAddSuccess] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [activityOpen, setActivityOpen] = useState(false)
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityError, setActivityError] = useState<string | null>(null)
  const [activity, setActivity] = useState<unknown[]>([])
  const [activityUserName, setActivityUserName] = useState<string>('')
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<string>('All')

  const fetchUsers = async (filter?: string) => {
      try {
        setError(null)
        const params: { page: number; limit: number; role?: string; status?: string } = { page: 1, limit: 100 }
        
        // Apply filter
        const currentFilter = filter || activeFilter
        if (currentFilter !== 'All') {
          if (currentFilter === 'Active') {
            params.status = 'active'
          } else if (currentFilter === 'Inactive') {
            params.status = 'inactive'
          } else {
            // It's a role filter
            params.role = currentFilter
          }
        }
        
        const res = await adminApi.users(params)
        const payload = res as unknown as { data?: { data?: AdminUser[]; pagination?: { total?: number } } }
        const data = payload.data?.data || []
        setUsers(Array.isArray(data) ? data : [])
        setTotalCount(payload.data?.pagination?.total ?? data.length)
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load users')
      }
  }

  useEffect(() => {
    void fetchUsers()
  }, [activeFilter])

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    setAddError(null)
    setAddSuccess(null)
    setAdding(true)
    try {
      await adminApi.createAdmin({
        email: addForm.email,
        phoneNumber: addForm.phoneNumber,
        password: addForm.password,
        firstName: addForm.firstName,
        lastName: addForm.lastName,
        ...(addForm.notes && { notes: addForm.notes }),
      })
      setAddSuccess('Admin created successfully.')
      setAddForm({
        email: '',
        phoneNumber: '',
        password: '',
        firstName: '',
        lastName: '',
        notes: '',
      })
      setAddOpen(false)
      await fetchUsers()
    } catch (err) {
      setAddError((err as Error)?.message || 'Failed to create admin')
    } finally {
      setAdding(false)
    }
  }

  const handleViewActivity = async (user: AdminUser) => {
    setActivityUserName(
      [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'User Activity'
    )
    setActivityOpen(true)
    setActivityError(null)
    setActivityLoading(true)
    try {
      const res = await adminApi.userActivity({ limit: 50 })
      const payload = res as { data?: { data?: unknown[] } | unknown[] }
      // Handle both nested { data: { data: [] } } and direct { data: [] } structures
      const activityData = payload.data
      if (Array.isArray(activityData)) {
        setActivity(activityData)
      } else if (activityData && typeof activityData === 'object' && 'data' in activityData) {
        setActivity(Array.isArray(activityData.data) ? activityData.data : [])
      } else {
        setActivity([])
      }
    } catch (err) {
      setActivityError((err as Error)?.message || 'Failed to load activity')
    } finally {
      setActivityLoading(false)
    }
  }

  const handleToggleStatus = async (user: AdminUser) => {
    if (updatingStatus) return // Prevent multiple simultaneous updates
    
    setUpdatingStatus(user.id)
    try {
      await adminApi.updateUserStatus(user.id, !user.isActive)
      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id ? { ...u, isActive: !u.isActive } : u
        )
      )
      setTotalCount(prev => prev) // Recalculate if needed
    } catch (err) {
      setError((err as Error)?.message || 'Failed to update user status')
      // Optionally show a toast notification
    } finally {
      setUpdatingStatus(null)
    }
  }

  const filters = useMemo(
    () => ['All', 'Active', 'Inactive', 'super_admin', 'organization_admin', 'counselor', 'couple'],
    []
  )

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter)
  }

  const handleSignOut = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' Users'
          infoText='Manage platform users, status, and roles.'
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <StatCard
          title='Total Users'
          value={totalCount.toLocaleString()}
          subtitle='All roles'
          trend=''
        />
        <StatCard
          title='Active'
          value={users.filter((u) => u.isActive).length.toLocaleString()}
          subtitle='isActive=true'
          trend=''
        />
        <StatCard
          title='Inactive'
          value={users.filter((u) => !u.isActive).length.toLocaleString()}
          subtitle='isActive=false'
          trend=''
        />
      </div>

      <div className='bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-4'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div className='flex flex-wrap gap-2'>
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => handleFilterClick(f)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                  activeFilter === f
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
            <Sheet open={addOpen} onOpenChange={setAddOpen}>
              <SheetTrigger asChild>
                <button className='px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800'>
                  Add Admin
                </button>
              </SheetTrigger>
              <SheetContent side='right' className='w-full sm:max-w-lg'>
                <SheetHeader>
                  <SheetTitle>Add Admin</SheetTitle>
                </SheetHeader>
                <form className='mt-6 space-y-4' onSubmit={handleCreateAdmin}>
                  {addError && (
                    <div className='text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2'>
                      {addError}
                    </div>
                  )}
                  {addSuccess && (
                    <div className='text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2'>
                      {addSuccess}
                    </div>
                  )}
                  <div className='space-y-1'>
                    <label className='text-xs font-semibold text-gray-700 text-start block'>First Name</label>
                    <input
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500'
                      required
                      value={addForm.firstName}
                      onChange={(e) => setAddForm((prev) => ({ ...prev, firstName: e.target.value }))}
                    />
                  </div>
                  <div className='space-y-1'>
                    <label className='text-xs font-semibold text-gray-700 text-start block'>Last Name</label>
                    <input
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500'
                      required
                      value={addForm.lastName}
                      onChange={(e) => setAddForm((prev) => ({ ...prev, lastName: e.target.value }))}
                    />
                  </div>
                  <div className='space-y-1'>
                    <label className='text-xs font-semibold text-gray-700 text-start block'>Email</label>
                    <input
                      type='email'
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500'
                      required
                      value={addForm.email}
                      onChange={(e) => setAddForm((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className='space-y-1'>
                    <label className='text-xs font-semibold text-gray-700 text-start block'>Phone Number</label>
                    <PhoneNumberInput
                      value={addForm.phoneNumber}
                      onChange={(value) => setAddForm((prev) => ({ ...prev, phoneNumber: value }))}
                      placeholder='Phone number'
                      required
                      containerClassName='border-gray-300'
                      inputClassName='py-0'
                    />
                  </div>
                  <div className='space-y-1'>
                    <label className='text-xs font-semibold text-gray-700 text-start block'>Password</label>
                    <input
                      type='password'
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500'
                      required
                      value={addForm.password}
                      onChange={(e) => setAddForm((prev) => ({ ...prev, password: e.target.value }))}
                      placeholder='Min 8 characters'
                    />
                  </div>
                  <div className='space-y-1'>
                    <label className='text-xs font-semibold text-gray-700 text-start block'>Notes</label>
                    <textarea
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500'
                      value={addForm.notes}
                      onChange={(e) => setAddForm((prev) => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <div className='flex justify-end gap-2 pt-2'>
                    <Button type='button' variant='outline' onClick={() => setAddOpen(false)} disabled={adding}>
                      Cancel
                    </Button>
                    <Button type='submit' className='bg-gray-900 hover:bg-gray-800' disabled={adding}>
                      {adding ? 'Creating...' : 'Create Admin'}
                    </Button>
                  </div>
                </form>
              </SheetContent>
            </Sheet>
          </div>

          <div className='border border-gray-200 rounded-lg overflow-hidden'>
            <div className='grid grid-cols-6 gap-2 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700 text-start'>
              <span>User</span>
            <span>Email</span>
            <span>Role</span>
            <span>Status</span>
            <span>Organization</span>
            <span className='text-right'>Actions</span>
          </div>
          {users.length === 0 ? (
            <div className='py-12 text-center'>
              <p className='text-sm text-gray-600'>No users found.</p>
              <p className='text-xs text-gray-500 mt-1'>Try adjusting your filters or add new users.</p>
            </div>
          ) : (
            users.map((item) => {
              const name = [item.firstName, item.lastName].filter(Boolean).join(' ') || item.email
              const email = item.email
              const role = item.role
              const isActive = item.isActive
              const org = item.organizationId || '—'
              return (
                <div
                  key={item.id}
                  className='grid grid-cols-6 gap-2 px-4 py-3 text-sm text-gray-800 border-t border-gray-100 items-center text-start'
                >
                  <span>{name}</span>
                  <span>{email}</span>
                  <span className='font-semibold text-gray-900'>{role}</span>
                  <span className={isActive ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span>{org}</span>
                  <div className='flex justify-end gap-2 text-xs'>
                    <button
                      className='px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                      onClick={() => handleToggleStatus(item)}
                      disabled={updatingStatus === item.id}
                    >
                      {updatingStatus === item.id
                        ? 'Updating...'
                        : !item.isActive
                        ? 'Reactivate'
                        : 'Deactivate'}
                    </button>
                    <button
                      className='px-3 py-1 rounded-md bg-orange-50 text-orange-600 hover:bg-orange-100'
                      onClick={() => handleViewActivity(item)}
                    >
                      View Activity
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
        {error && <p className='text-sm text-red-600'>{error}</p>}
        <p className='text-xs text-gray-500'>
          Table powered by admin user endpoints with status toggles and bulk actions.
        </p>
      </div>

      <Sheet open={activityOpen} onOpenChange={setActivityOpen}>
        <SheetContent side='right' className='w-full sm:max-w-xl'>
          <SheetHeader>
            <SheetTitle>{activityUserName || 'User Activity'}</SheetTitle>
          </SheetHeader>
          <div className='mt-4 space-y-3'>
            {activityError && (
              <div className='text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2'>
                {activityError}
              </div>
            )}
            {activityLoading && <p className='text-sm text-gray-600'>Loading activity...</p>}
            {!activityLoading && !activityError && (
              <div className='space-y-2 max-h-[70vh] overflow-y-auto'>
                {activity.length === 0 && <p className='text-sm text-gray-600'>No activity found.</p>}
                {activity.map((entry, idx) => (
                  <div
                    key={idx}
                    className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800'
                  >
                    <pre className='text-xs whitespace-pre-wrap break-words'>{JSON.stringify(entry, null, 2)}</pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
